import * as yup from 'yup/es';
import { BaseSchema } from 'yup/es';
import ObjectSchema, { Assign } from 'yup/es/object';
import { AnyObject } from 'yup/es/types';
import StringSchema, { RequiredStringSchema } from 'yup/es/string';
import Lazy from 'yup/es/Lazy';

export const isValidYupSchema = async (schema: any, value: any): Promise<boolean> => {
  return !!(await schema.validate(value, { strict: true }));
};

export const validateYup = async <
  TSchema extends BaseSchema<TCast, TContext, TOutput>,
  TCast = unknown,
  TContext = AnyObject,
  TOutput = unknown,
>(
  schema: TSchema,
  value: unknown,
): Promise<boolean> => {
  return !!(await schema.validate(value, { strict: true }));
};

export const mergeSchemas = <
  TSchema extends BaseSchema<TCast, TContext, TOutput>,
  TCast = unknown,
  TContext = AnyObject,
  TOutput = unknown,
>(
  schemas: TSchema[],
): TSchema => {
  const [first, ...rest] = schemas;
  return rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);
};

const mapRules = <T>(map: Record<string, unknown>, rule: T): Record<string, T> => {
  return Object.keys(map).reduce((newMap, key) => ({ ...newMap, [key]: rule }), {});
};

yup.addMethod(yup.MixedSchema, 'oneOfSchemas', function (schemas: yup.AnySchema[]) {
  return this.test('one-of-schemas', 'Not all items in ${path} match one of the allowed schemas', (item) =>
    schemas.some((schema) => schema.isValidSync(item, { strict: true })),
  );
});

export const yupUtils: any = {
  stringRequired: (message?: string) => yup.string().required(message),
  stringOptionalNullable: yup.string().nullable().optional(),
  booleanRequired: yup.boolean().required(),
  booleanOptionalNullable: yup.boolean().optional().nullable(), // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arrayOptionalNullableOf: (schema: ObjectSchema<any> | StringSchema<any>) =>
    yup.array().of(schema).optional().nullable(), // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arrayRequiredOf: (schema: ObjectSchema<any> | StringSchema<any>) => yup.array().of(schema).required(),
  arrayOfOptionalStrings: yup.array().of(yup.string().optional()).required(),
  arrayOfOptionalNumbers: yup.array().of(yup.number().optional()).required(),
  numberRequired: yup.number().required(),
  numberOptionalNullable: yup.number().optional().nullable(),
  dateRequired: yup.date().required(),

  stringRequiredOneOf: <T extends string>(arrayEnum: T[]) => {
    const stringSchema: RequiredStringSchema<T> = new StringSchema<T>().required();
    return stringSchema.oneOf<T>([...arrayEnum]);
  },
  stringNullableOneOf: <T extends string>(arrayEnum: T[]) => {
    const stringSchema: StringSchema<T | null> = new StringSchema<T>().nullable().optional();
    return stringSchema.oneOf<T | null>([...arrayEnum, null]);
  },
  enumRequired: <T extends string>(enumObject: Record<string, T>) => {
    return yupUtils.stringRequiredOneOf(Object.values(enumObject));
  },
  enumNullable: <T extends string>(enumObject: Record<string, T>) => {
    return yupUtils.stringNullableOneOf(Object.values(enumObject));
  },
  enumUnionNullable: <T1 extends string, T2 extends string>(
    enumObject1: Record<string, T1>,
    enumObject2: Record<string, T2>,
  ) => {
    return yupUtils.stringNullableOneOf([...Object.values(enumObject1), ...Object.values(enumObject2)]);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dynamicObjectKeysRequired: (objectSchema: ObjectSchema<any> | Lazy<any>) =>
    yup.lazy((map) => yup.object(mapRules<typeof objectSchema>(map, objectSchema))),
};
